import React, { useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import {
  Typography, Box, Button, CircularProgress,
  Dialog, DialogContent, DialogTitle, DialogActions,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useAptorApi } from '../../../../../Api';
import { AccessLevel, LawPortalFeature, UserContext } from '../../../../../Context/UserContext/UserContext';
import { useIntl } from 'react-intl';
import moment from 'moment';

import { IProps, StepData, WizardSession } from './types';
import { saveWizardSession, clearWizardSession, loadWizardSession } from './helpers';
import { outlinedRedBtn } from './StepButtonBar';
import StepSelectFlow from './StepSelectFlow';
import StepSelectPlanType from './StepSelectPlanType';
import Step0SelectPlan from './Step0SelectPlan';
import Step1Days from './Step1Days';
import Step2CompanyUnit from './Step2CompanyUnit';
import Step3Dates from './Step3Dates';
import Step4Reminder from './Step4Reminder';
import Step5RequirementType from './Step5RequirementType';
import Step6LawsTable from './Step6LawsTable';
import Step7PlanName from './Step7PlanName';
import Step8Preview from './Step8Preview';

export { saveWizardSession, clearWizardSession, loadWizardSession };
export type { WizardSession };

const PRIMARY = '#c4122f';

export const CreatePlanWizard = ({
  open,
  planType: propPlanType,
  onClose,
  getCompliancePlanList,
  editPlan,
  resumeSession,
  evalutionStepInfo,
  onEvaluationStepUpdate,
}: IProps) => {
  const { companyUnits, customerId, id: userId, hasAccessToFeature } = useContext(UserContext);
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCompanyUnit, setSelectedCompanyUnit] = useState<any>(null);
  const [selectedRequirementType, setSelectedRequirementType] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stepData, setStepData] = useState<StepData>({});
  const [editInitialized, setEditInitialized] = useState(!editPlan);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [currentEvalutionStepInfo, setCurrentEvalutionStepInfo] = useState<any>(evalutionStepInfo);

  useEffect(() => {
    setCurrentEvalutionStepInfo(evalutionStepInfo);
  }, [evalutionStepInfo]);

  const handleEvalutionStepInfoUpdate = useCallback(() => {
    if (onEvaluationStepUpdate) {
      onEvaluationStepUpdate();
    }
  }, [onEvaluationStepUpdate]);

  const resolvedFlow: 'create' | 'from-completed' =
    propPlanType === 'start'
      ? (stepData._resolvedFlow ?? 'create')
      : propPlanType === 'from-completed'
        ? 'from-completed'
        : 'create';

  const resolvedPlanType: 'one-time' | 'recurring' | 'from-completed' =
    propPlanType === 'create' || propPlanType === 'start'
      ? (stepData._resolvedPlanType ?? 'one-time')
      : (propPlanType as 'one-time' | 'recurring' | 'from-completed');

  const sessionHydrated = useRef(false);

  const REMINDER_OPTIONS = useMemo(() => [
    { id: 0, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.week' }, { number: 1 }) },
    { id: 1, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.weeks' }, { number: 2 }) },
    { id: 2, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.weeks' }, { number: 3 }) },
    { id: 3, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.month' }, { number: 1 }) },
    { id: 4, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.months' }, { number: 2 }) },
    { id: 5, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.months' }, { number: 3 }) },
    { id: 6, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.months' }, { number: 6 }) },
    { id: 7, name: formatMessage({ id: 'law-portal.compliance.create.plan.remainder.year' }, { number: 1 }) },
  ], [formatMessage]);

  const accessToOurLaws = hasAccessToFeature(LawPortalFeature.OurLaws, AccessLevel.Read);
  const accessToOtherRequirements = hasAccessToFeature(LawPortalFeature.OtherRequirements, AccessLevel.Read);

  const requirementTypeOptions = useMemo(() => {
    const opts: { id: number; name: string }[] = [];
    if (accessToOurLaws) opts.push({ id: 0, name: formatMessage({ id: 'law-portal.plan.type.law', defaultMessage: 'Legislation' }) });
    if (accessToOtherRequirements) opts.push({ id: 1, name: formatMessage({ id: 'law-portal.plan.type.other-req', defaultMessage: 'Other requirements' }) });
    return opts;
  }, [accessToOurLaws, accessToOtherRequirements, formatMessage]);

  useEffect(() => {
    if (editPlan || sessionHydrated.current || !resumeSession) return;
    if (resumeSession.currentStep <= 2) {
      clearWizardSession(userId);
      return;
    }
    sessionHydrated.current = true;
    setCurrentStep(resumeSession.currentStep);
    setStepData(resumeSession.stepData);
    setSelectedRows(resumeSession.selectedRows ?? []);
    setSelectedRowsData(resumeSession.selectedRowsData ?? []);
    const compUnit = companyUnits.find(u => u.id === resumeSession.stepData.CustomerCompanyUnitId);
    const reqType = requirementTypeOptions.find(t => t.id === resumeSession.stepData.type);
    if (compUnit) setSelectedCompanyUnit(compUnit);
    if (reqType) setSelectedRequirementType(reqType);
    setEditInitialized(true);
  }, [resumeSession, editPlan, companyUnits, requirementTypeOptions]);

  useEffect(() => {
    if (!editPlan) {
      if (!resumeSession) {
        if (companyUnits.length === 1) {
          setSelectedCompanyUnit(companyUnits[0]);
          setStepData(prev => ({ ...prev, CustomerCompanyUnitId: companyUnits[0].id, _companyUnitName: companyUnits[0].name }));
        }
        if (requirementTypeOptions.length === 1) {
          setSelectedRequirementType(requirementTypeOptions[0]);
          setStepData(prev => ({ ...prev, type: requirementTypeOptions[0].id, _typeName: requirementTypeOptions[0].name }));
        }
      }
      return;
    }
    const compUnit = companyUnits.find(u => u.id === (editPlan.customerCompanyUnitId?.id ?? editPlan.customerCompanyUnitId));
    const reqType = requirementTypeOptions.find(t => t.id === editPlan.type);
    const reminderOpt = editPlan.reminder != null ? REMINDER_OPTIONS.find((o: any) => o.id === editPlan.reminder) : null;
    if (compUnit) setSelectedCompanyUnit(compUnit);
    if (reqType) setSelectedRequirementType(reqType);
    setStepData({
      _editPlanId: editPlan.id ?? editPlan._planId,
      name: editPlan.name,
      CustomerCompanyUnitId: compUnit?.id,
      _companyUnitName: compUnit?.name || editPlan.customerCompanyUnitId?.name,
      StartDate: editPlan.startDate ? moment(editPlan.startDate).format('YYYY-MM-DD') : undefined,
      EndDate: editPlan.endDate ? moment(editPlan.endDate).format('YYYY-MM-DD') : undefined,
      type: editPlan.type,
      _typeName: reqType?.name,
      RecurrentInterval: editPlan.recurrentInterval || undefined,
      Reminder: editPlan.reminder,
      _reminderLabel: reminderOpt?.name,
      _sendReminder: editPlan.reminder != null,
    });
    const fetchLaws = async () => {
      try {
        const result = await api.getPlanAllLawID(editPlan.id ?? editPlan._planId);
        setSelectedRows(result?.legislationIds || []);
      } catch (e) {
        console.error('Error fetching plan laws for edit:', e);
      } finally {
        setEditInitialized(true);
      }
    };
    fetchLaws();
  }, [editPlan, companyUnits, requirementTypeOptions]);

  useEffect(() => {
    if (!open || editPlan) return;
    if (
      (propPlanType === 'start' || propPlanType === 'create') &&
      !stepData._resolvedPlanType &&
      !stepData._resolvedFlow
    ) return;
    if (currentStep <= 2) return;
    const session: WizardSession = {
      planType: propPlanType,
      currentStep,
      stepData,
      selectedRows,
      selectedRowsData,
    };
    saveWizardSession(userId, session);
  }, [open, editPlan, propPlanType, currentStep, stepData, selectedRows, selectedRowsData, userId]);

  // Determine if company unit step should be skipped
  // Skip if: company account has only one unit OR user is connected to only one unit
  const shouldSkipCompanyUnitStep = useMemo(() => {
    return companyUnits.length === 1;
  }, [companyUnits.length]);

  // Auto-populate company unit if skipping (for new, edit, and resume modes)
  useEffect(() => {
    if (shouldSkipCompanyUnitStep && companyUnits.length === 1 && !stepData.CustomerCompanyUnitId) {
      const unit = companyUnits[0];
      setSelectedCompanyUnit(unit);
      setStepData(prev => ({
        ...prev,
        CustomerCompanyUnitId: unit.id,
        _companyUnitName: unit.name,
      }));
    }
  }, [shouldSkipCompanyUnitStep, companyUnits, stepData.CustomerCompanyUnitId]);

  const getActiveSteps = useCallback(() => {
    const steps: string[] = [];
    if (propPlanType === 'start') {
      steps.push('select-flow');
      if (resolvedFlow === 'from-completed') {
        steps.push('select-plan');
        if (resolvedPlanType === 'recurring') steps.push('days');
      } else {
        steps.push('select-plan-type');
        if (resolvedPlanType === 'recurring') steps.push('days');
      }
    } else if (propPlanType === 'create') {
      steps.push('select-plan-type');
      if (resolvedPlanType === 'recurring') steps.push('days');
    } else {
      if (propPlanType === 'from-completed') steps.push('select-plan');
      if (propPlanType === 'recurring') steps.push('days');
      if (propPlanType === 'from-completed' && resolvedPlanType === 'recurring') steps.push('days');
    }
    // Only add company-unit step if it should not be skipped
    if (!shouldSkipCompanyUnitStep) {
      steps.push('company-unit');
    }
    steps.push('dates');
    steps.push('reminder');
    steps.push('requirement-type');
    steps.push('laws-table');
    steps.push('plan-name');
    steps.push('preview');
    return steps;
  }, [propPlanType, resolvedFlow, resolvedPlanType, shouldSkipCompanyUnitStep]);

  const activeSteps = useMemo(() => getActiveSteps(), [getActiveSteps]);
  const currentStepName = activeSteps[currentStep - 1];
  const totalSteps = activeSteps.length;
  const isFirstStep = currentStep === 1;

  const handleStepSubmit = (data: Partial<StepData>) => {
    if (
      (data.CustomerCompanyUnitId !== undefined && data.CustomerCompanyUnitId !== stepData.CustomerCompanyUnitId) ||
      (data.type !== undefined && data.type !== stepData.type)
    ) {
      setSelectedRows([]);
      setSelectedRowsData([]);
    }
    setStepData(prev => ({ ...prev, ...data }));
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const closeWizard = () => {
    setCurrentStep(1);
    setSelectedCompanyUnit(null);
    setSelectedRequirementType(null);
    setSelectedRows([]);
    setSelectedRowsData([]);
    setStepData({});
    onClose();
  };

  const handleCancel = () => {
    const isEarlyStep = currentStep <= 2;
    if (isEarlyStep || editPlan) {
      closeWizard();
    } else {
      setShowCancelConfirm(true);
    }
  };

  const handleCancelWithoutSaving = () => {
    setShowCancelConfirm(false);
    if (!editPlan) {
      clearWizardSession(userId);
    }
    closeWizard();
  };

  const handleSaveAndCancel = () => {
    setShowCancelConfirm(false);
    if (!editPlan && currentStep >= 3) {
      saveWizardSession(userId, {
        planType: propPlanType,
        currentStep,
        stepData,
        selectedRows,
        selectedRowsData,
      });
    }
    closeWizard();
  };

  const handleFinalSubmit = async (previewData?: StepData) => {
    const finalData = previewData || stepData;
    const effectiveIsDirect =
      propPlanType === 'create' || propPlanType === 'start'
        ? finalData._resolvedPlanType === 'one-time'
        : propPlanType === 'one-time';
    try {
      setIsLoading(true);
      const apiData: any = {
        name: finalData.name,
        CustomerCompanyUnitId: finalData.CustomerCompanyUnitId,
        StartDate: finalData.StartDate,
        EndDate: finalData.EndDate,
        type: finalData.type,
        isDirect: effectiveIsDirect,
      };
      if (finalData.Reminder != null) apiData.Reminder = finalData.Reminder;
      if (finalData._resolvedPlanType === 'recurring' || finalData.RecurrentInterval) {
        apiData.RecurrentInterval = finalData.RecurrentInterval;
      }
      if (finalData._editPlanId) {
        await api.updatePlanLaws(finalData._editPlanId, apiData);
        await api.addLaws(finalData._editPlanId, { Legislations: selectedRows, type: finalData.type });
      } else {
        const response: any = await api.createCompliancePlan(customerId, apiData);
        if (response?.id) {
          await api.addLaws(response.id, { Legislations: selectedRows, type: finalData.type });
        }
      }
      if (!editPlan) {
        clearWizardSession(userId);
      }
      closeWizard();
      getCompliancePlanList();
    } catch (error) {
      console.error('Error saving plan:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;
  if (!editInitialized) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderStepContent = () => {
    const common = { onBack: handleBack, onCancel: handleCancel, currentData: stepData, isFirstStep, currentStepName };

    switch (currentStepName) {
      case 'select-flow':
        return (
          <StepSelectFlow
            {...common}
            evalutionStepInfo={currentEvalutionStepInfo}
            onUpdateEvalution={handleEvalutionStepInfoUpdate}
            onNext={(flow) => {
              if (flow !== stepData._resolvedFlow) {
                setSelectedRows([]);
                setSelectedRowsData([]);
                setStepData(prev => ({
                  ...prev,
                  _resolvedFlow: flow,
                  _resolvedPlanType: flow === 'create' ? prev._resolvedPlanType : undefined,
                }));
              } else {
                setStepData(prev => ({ ...prev, _resolvedFlow: flow }));
              }
              setCurrentStep(prev => Math.min(prev + 1, totalSteps));
            }}
          />
        );

      case 'select-plan-type':
        return (
          <StepSelectPlanType
            evalutionStepInfo={currentEvalutionStepInfo}
            onUpdateEvalution={handleEvalutionStepInfoUpdate}
            {...common}
            onNext={(resolved) => {
              setStepData(prev => ({ ...prev, _resolvedPlanType: resolved }));
              setCurrentStep(prev => Math.min(prev + 1, totalSteps));
            }}
          />
        );

      case 'select-plan':
        return (
          <Step0SelectPlan
            evalutionStepInfo={currentEvalutionStepInfo}
            onUpdateEvalution={handleEvalutionStepInfoUpdate}
            {...common}
            customerId={customerId}
            api={api}
            requirementTypeOptions={requirementTypeOptions}
            companyUnits={companyUnits}
            onPlanSelected={(data, compUnit, reqType, rows, planId) => {
              const isNewPlan = planId !== stepData._selectedPlanId;
              if (isNewPlan) {
                setStepData(prev => ({
                  ...prev,
                  ...data,
                  _selectedPlanId: planId,
                  name: undefined,
                }));
                if (compUnit) setSelectedCompanyUnit(compUnit);
                if (reqType) setSelectedRequirementType(reqType);
                setSelectedRows(rows);
                setSelectedRowsData([]);
              } else {
                setStepData(prev => ({ ...prev, _selectedPlanId: planId }));
              }
              setCurrentStep(prev => Math.min(prev + 1, totalSteps));
            }}
          />
        );

      case 'days':
        return <Step1Days evalutionStepInfo={currentEvalutionStepInfo} onUpdateEvalution={handleEvalutionStepInfoUpdate} {...common} onNext={handleStepSubmit} />;

      case 'company-unit':
        return (
          <Step2CompanyUnit
            evalutionStepInfo={currentEvalutionStepInfo}
            onUpdateEvalution={handleEvalutionStepInfoUpdate}
            {...common}
            companyUnits={companyUnits}
            onNext={handleStepSubmit}
            onCompanyUnitSelect={setSelectedCompanyUnit}
          />
        );

      case 'dates':
        return <Step3Dates evalutionStepInfo={currentEvalutionStepInfo} onUpdateEvalution={handleEvalutionStepInfoUpdate} {...common} onNext={handleStepSubmit} />;

      case 'reminder':
        return <Step4Reminder evalutionStepInfo={currentEvalutionStepInfo} onUpdateEvalution={handleEvalutionStepInfoUpdate} {...common} onNext={handleStepSubmit} reminderOptions={REMINDER_OPTIONS} />;

      case 'requirement-type':
        return (
          <Step5RequirementType
            evalutionStepInfo={currentEvalutionStepInfo}
            onUpdateEvalution={handleEvalutionStepInfoUpdate}
            {...common}
            typeOptions={requirementTypeOptions}
            onNext={handleStepSubmit}
            onTypeSelect={setSelectedRequirementType}
          />
        );

      case 'laws-table':
        return (
          <Step6LawsTable
            currentStepName={currentStepName}
            evalutionStepInfo={currentEvalutionStepInfo}
            onUpdateEvalution={handleEvalutionStepInfoUpdate}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={setSelectedRowsData}
            selectedRequirementType={selectedRequirementType}
            selectedCompanyUnit={selectedCompanyUnit}
            onNext={() => setCurrentStep(prev => prev + 1)}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );

      case 'plan-name':
        return <Step7PlanName evalutionStepInfo={currentEvalutionStepInfo} onUpdateEvalution={handleEvalutionStepInfoUpdate} {...common} onNext={handleStepSubmit} />;

      case 'preview':
        return (
          <Step8Preview
            planData={stepData}
            setStepData={setStepData}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={setSelectedRowsData}
            requirementType={selectedRequirementType}
            setSelectedCompanyUnit={setSelectedCompanyUnit}
            setSelectedRequirementType={setSelectedRequirementType}
            companyUnits={companyUnits}
            requirementTypeOptions={requirementTypeOptions}
            reminderOptions={REMINDER_OPTIONS}
            onSubmit={handleFinalSubmit}
            onBack={handleBack}
            onCancel={handleCancel}
            isLoading={isLoading}
            planType={
              propPlanType === 'create' || propPlanType === 'start'
                ? (stepData._resolvedPlanType ?? 'one-time')
                : propPlanType
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} disableEscapeKeyDown maxWidth="lg" fullWidth>
      <DialogTitle>
        {editPlan
          ? formatMessage({ id: "law-portal.legal.compliance.sub-menu.planning.btn.edit-evealution", defaultMessage: "EDIT EVALUATION CONTROL" })
          : formatMessage({ id: "law-portal.legal.compliance.sub-menu.planning.btn.start-evealution", defaultMessage: "CREATE EVALUATION CONTROL" })
        }
      </DialogTitle>
      <DialogContent style={{ padding: '24px' }}>
        <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Button variant="outlined" onClick={handleCancel} style={outlinedRedBtn}>
              {formatMessage({ id: 'form.cancel' })}
            </Button>
          </Box>
          {!isFirstStep && (
            <Button variant="outlined" onClick={handleBack} style={outlinedRedBtn}>
              {formatMessage({ id: 'component.back-button.back' })}
            </Button>
          )}
        </Box>

        <Box style={{ minHeight: '420px' }}>
          {/* Step Indicator Bar */}
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 16px',
              marginBottom: '12px',
              backgroundColor: '#fdf2f3',
              borderRadius: '6px',
              borderLeft: `4px solid ${PRIMARY}`,
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <Typography variant="body2" style={{ fontWeight: 700, color: PRIMARY, fontSize: '13px' }}>
              {formatMessage({ id: "law-portal.evalution.plan.step" }, { currentStep, totalSteps })}
            </Typography>

            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              {activeSteps.map((_, idx) => (
                <Box
                  key={idx}
                  style={{
                    width: idx === currentStep - 1 ? '20px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: idx < currentStep ? PRIMARY : '#e0e0e0',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>

            <Typography variant="body2" style={{ fontWeight: 600, color: '#555', fontSize: '13px', textTransform: 'capitalize' }} />
          </Box>

          {renderStepContent()}
        </Box>
      </DialogContent>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)} maxWidth="sm">
        <DialogTitle>
          <div style={{ cursor: 'pointer' }} onClick={() => setShowCancelConfirm(false)}><Close /></div>
          {formatMessage({
            id: 'law-portal.evalution.cancel.confirm.title',
            defaultMessage: 'Do you want to cancel and save ongoing work?',
          })}
        </DialogTitle>
        <DialogActions style={{
          padding: '12px',
          justifyContent: 'space-between',
          gap: '8px',
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}>
          <Button
            variant="outlined"
            onClick={handleCancelWithoutSaving}
            style={{ color: PRIMARY, borderColor: PRIMARY, flex: 1, minWidth: '110px', padding: '10px 16px', fontSize: '14px' }}
          >
            {formatMessage({ id: 'law-portal.evalution.cancel.without.save', defaultMessage: 'Cancel without saving' })}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAndCancel}
            style={{ backgroundColor: PRIMARY, color: '#fff', flex: 1, minWidth: '110px', padding: '10px 16px', fontSize: '14px' }}
          >
            {formatMessage({ id: 'law-portal.evalution.save.and.cancel', defaultMessage: 'Save and cancel' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CreatePlanWizard;